import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';

interface MonthlyData {
    name: string; // "Jan 2024"
    revenue: number;
    expense: number;
    net: number;
}

interface SourceData {
    name: string;
    value: number;
    color: string;
}

const Revenue: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
    const [sourceData, setSourceData] = useState<SourceData[]>([]);
    const [totals, setTotals] = useState({ revenue: 0, expense: 0, net: 0 });
    const [year, setYear] = useState(new Date().getFullYear());

    useEffect(() => {
        fetchData();
    }, [year]);

    const convertToEur = (amount: number, currency: string) => {
        if (!amount) return 0;
        if (currency === 'EUR') return amount;
        if (currency === 'USD') return amount * 0.92; // Approx rate
        if (currency === 'TRY') return amount / 38;   // Approx rate
        return amount;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch Course Payments (Income)
            const { data: coursePayments } = await supabase
                .from('testprep_payments')
                .select('installments, currency');

            // 2. Fetch Private Lesson Packages (Income)
            const { data: lessonPackages } = await supabase
                .from('testprep_lesson_packages')
                .select('price, currency, created_at, is_paid')
                .eq('is_paid', true);

            // 3. Fetch Teacher Payments (Expense)
            const { data: teacherPayments } = await supabase
                .from('testprep_teacher_payments')
                .select('total_amount, currency, payment_date')
                .eq('status', 'paid');

            // 4. Fetch Course Enrollments (Direct Income)
            const { data: enrollments } = await supabase
                .from('testprep_enrollments')
                .select('payment_amount, enrollment_date, is_paid')
                .eq('is_paid', true)
                .not('payment_amount', 'is', null);

            // Process Data
            const months = Array.from({ length: 12 }, (_, i) => {
                const d = new Date(year, i, 1);
                return {
                    name: d.toLocaleString('tr-TR', { month: 'short' }),
                    revenue: 0,
                    expense: 0,
                    net: 0,
                    year: year,
                    monthIndex: i
                };
            });

            let totalRev = 0;
            let totalExp = 0;
            let courseRev = 0;
            let lessonRev = 0;
            let enrollmentRev = 0;

            // Process Course Payments
            if (coursePayments) {
                coursePayments.forEach(plan => {
                    const installments = plan.installments as any[];
                    if (Array.isArray(installments)) {
                        installments.forEach(inst => {
                            if (inst.status === 'paid' && inst.date) {
                                const d = new Date(inst.date);
                                if (d.getFullYear() === year) {
                                    const amountEur = convertToEur(Number(inst.amount), plan.currency);
                                    months[d.getMonth()].revenue += amountEur;
                                    totalRev += amountEur;
                                    courseRev += amountEur;
                                }
                            }
                        });
                    }
                });
            }

            // Process Lesson Packages
            if (lessonPackages) {
                lessonPackages.forEach(pkg => {
                    const d = new Date(pkg.created_at); // Assuming payment date is close to creation for now
                    if (d.getFullYear() === year) {
                        const amountEur = convertToEur(Number(pkg.price), pkg.currency);
                        months[d.getMonth()].revenue += amountEur;
                        totalRev += amountEur;
                        lessonRev += amountEur;
                    }
                });
            }

            // Process Enrollments
            if (enrollments) {
                enrollments.forEach(enroll => {
                    // Assuming enrollment_date is YYYY-MM-DD
                    const d = new Date(enroll.enrollment_date);
                    if (d.getFullYear() === year) {
                        // Assuming default EUR for now as no currency field in enrollments
                        const amountEur = Number(enroll.payment_amount || 0);
                        months[d.getMonth()].revenue += amountEur;
                        totalRev += amountEur;
                        enrollmentRev += amountEur;
                    }
                });
            }

            // Process Teacher Payments
            if (teacherPayments) {
                teacherPayments.forEach(pay => {
                    if (pay.payment_date) {
                        const d = new Date(pay.payment_date);
                        if (d.getFullYear() === year) {
                            const amountEur = convertToEur(Number(pay.total_amount), pay.currency);
                            months[d.getMonth()].expense += amountEur;
                            totalExp += amountEur;
                        }
                    }
                });
            }

            // Calculate Net
            months.forEach(m => {
                m.net = m.revenue - m.expense;
            });

            setMonthlyData(months);
            setTotals({
                revenue: totalRev,
                expense: totalExp,
                net: totalRev - totalExp
            });

            setSourceData([
                { name: 'Kurs Taksitleri', value: courseRev, color: '#0088FE' },
                { name: 'Özel Dersler', value: lessonRev, color: '#00C49F' },
                { name: 'Kurs Kayıtları (Peşin/Tek)', value: enrollmentRev, color: '#FFBB28' }
            ]);

        } catch (error) {
            console.error('Error fetching revenue data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val: number) => `€${val.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gelir Takibi</h2>
                    <p className="text-gray-600 dark:text-gray-400">Finansal durum özeti ve raporlar</p>
                </div>
                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                    <button
                        onClick={() => setYear(year - 1)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                        ←
                    </button>
                    <span className="font-medium px-2">{year}</span>
                    <button
                        onClick={() => setYear(year + 1)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                        →
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg text-green-600">
                            <TrendingUp className="w-6 h-6" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Yıllık Toplam</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {formatCurrency(totals.revenue)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Gelir</p>
                </div>

                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg text-red-600">
                            <TrendingDown className="w-6 h-6" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Yıllık Toplam</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {formatCurrency(totals.expense)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Gider (Hoca)</p>
                </div>

                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg text-blue-600">
                            <DollarSign className="w-6 h-6" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Yıllık Toplam</span>
                    </div>
                    <h3 className={`text-3xl font-bold mb-1 ${totals.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(totals.net)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Net Kâr</p>
                </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Monthly Chart */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Aylık Gelir/Gider Analizi</h3>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} tickFormatter={(val) => `€${val}`} />
                                <Tooltip
                                    formatter={(value: number) => [`€${value.toLocaleString()}`, '']}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                />
                                <Legend />
                                <Bar dataKey="revenue" name="Gelir" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="expense" name="Gider" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Source Chart */}
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Gelir Kaynakları</h3>
                    <div className="h-64 w-full flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={sourceData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {sourceData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(val: number) => `€${val.toLocaleString()}`} />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-3">
                        {sourceData.map((item, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                                    <span className="text-gray-600 dark:text-gray-400">{item.name}</span>
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {((item.value / (totals.revenue || 1)) * 100).toFixed(1)}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Revenue;
